import { useEffect, useState } from "react";
import { Link, useNavigate, useParams } from "react-router-dom";
import axios from "axios";
import { API } from "@/lib/api";
import { formatRp } from "@/lib/constants";
import { Lightning, Timer, CheckCircle, Warning } from "@phosphor-icons/react";
import { PrivacyConsentBox, DataSafeNote, CONSENT_VERSION } from "@/components/PrivacyConsent";

const inp = "w-full rounded-xl border border-slate-200 px-3.5 py-2.5 text-sm outline-none focus:border-rose-400";
const mmss = (s) => `${String(Math.floor(s / 60)).padStart(2, "0")}:${String(s % 60).padStart(2, "0")}`;
const idemKey = () => {
  let k = sessionStorage.getItem("gbs_now_idem");
  if (!k) { k = `${Date.now()}-${Math.random().toString(36).slice(2, 12)}`; sessionStorage.setItem("gbs_now_idem", k); }
  return k;
};

function BookForm({ d, tcText }) {
  const [f, setF] = useState({ nama_lengkap: "", no_whatsapp: "", tanggal_lahir: "", keluhan: "", consent: false, privacy: false });
  const [err, setErr] = useState("");
  const [busy, setBusy] = useState(false);
  const nav = useNavigate();
  const submit = async (e) => {
    e.preventDefault(); setErr("");
    if (!f.consent) return setErr("Persetujuan telekonsultasi wajib dicentang.");
    setBusy(true);
    try {
      const r = await axios.post(`${API}/public/consult-now/book`, { ...f, telekonsultasi_consent: f.consent, consent_privacy: f.privacy, consent_version: CONSENT_VERSION, service_id: d.service_id, petugas_nama: d.petugas_nama, idem_key: idemKey() });
      sessionStorage.removeItem("gbs_now_idem");
      nav(`/konsultasi-sekarang/status/${r.data.token}`);
    } catch (ex) { setErr(ex?.response?.data?.detail || "Gagal memesan. Coba lagi."); sessionStorage.removeItem("gbs_now_idem"); }
    finally { setBusy(false); }
  };
  const set = (k) => (e) => setF({ ...f, [k]: e.target.value });
  return (
    <form onSubmit={submit} className="mt-3 space-y-2.5" data-testid="now-form">
      <input data-testid="now-nama" className={inp} placeholder="Nama lengkap *" value={f.nama_lengkap} onChange={set("nama_lengkap")} required />
      <input data-testid="now-wa" className={inp} placeholder="No. WhatsApp *" value={f.no_whatsapp} onChange={set("no_whatsapp")} required />
      <input data-testid="now-dob" className={inp} type="date" value={f.tanggal_lahir} onChange={set("tanggal_lahir")} />
      <textarea data-testid="now-keluhan" className={inp} rows={2} placeholder="Keluhan singkat (opsional)" value={f.keluhan} onChange={set("keluhan")} />
      <DataSafeNote />
      <label className="flex gap-2 text-[11px] text-slate-600"><input data-testid="now-consent" type="checkbox" checked={f.consent} onChange={(e) => setF({ ...f, consent: e.target.checked })} /> <span>{tcText || "Saya menyetujui layanan telekonsultasi."}</span></label>
      <PrivacyConsentBox checked={f.privacy} onChange={(v) => setF({ ...f, privacy: v })} testid="now-privacy-consent" />
      {err && <p data-testid="now-error" className="rounded-lg bg-rose-50 p-2 text-xs text-rose-700">{err}</p>}
      <button data-testid="now-submit" disabled={busy || !f.privacy} className="w-full rounded-full bg-[#EC4899] py-2.5 text-sm font-bold text-white disabled:opacity-60">{busy ? "Memproses…" : `Lanjut Bayar ${formatRp(d.harga)}`}</button>
      <p className="text-[11px] text-slate-500">Tempat ditahan 15 menit untuk pembayaran. Transfer manual menunggu verifikasi admin, sehingga konsultasi tidak dijamin langsung dimulai.</p>
      <p data-testid="now-form-notes" className="text-[11px] text-slate-500">Pemesanan ditolak bila waktu konsultasi melewati pukul 00.00 WITA. Tindak lanjut admin (ganti petugas, jadwal ulang, refund) di luar jam kerja dapat menunggu jam operasional.</p>
    </form>
  );
}

export default function ConsultNow() {
  const [data, setData] = useState(null);
  const [pick, setPick] = useState(null);
  const [tcText, setTcText] = useState("");
  useEffect(() => {
    document.title = "Konsultasi Sekarang — GBS";
    const load = () => axios.get(`${API}/public/consult-now/doctors`).then((r) => setData(r.data)).catch(() => setData({ doctors: [] }));
    load(); axios.get(`${API}/consult/consent-text`).then((r) => setTcText(r.data.text)).catch(() => {});
    const id = setInterval(load, 30000); return () => clearInterval(id);
  }, []);
  return (
    <div className="mx-auto min-h-screen max-w-lg bg-[#FFF7FA] p-4" data-testid="consult-now-page">
      <h1 className="flex items-center gap-2 font-heading text-2xl font-extrabold text-[#DB2777]"><Lightning size={24} weight="fill" /> Konsultasi Sekarang</h1>
      <p className="mt-1 text-sm text-slate-600">Chat langsung dengan petugas kesehatan yang sedang tersedia. Durasi dihitung sejak petugas menerima sesi. Tidak dapat dipesan bila waktunya melewati pukul 00.00 WITA.</p>
      <Link to="/booking" data-testid="now-to-scheduled" className="mt-2 inline-block text-xs font-semibold text-rose-600 underline">Atau booking konsultasi terjadwal</Link>
      {data === null && <p className="mt-6 text-sm text-slate-400">Memuat…</p>}
      {data && data.doctors.length === 0 && <p data-testid="now-empty" className="mt-6 rounded-2xl border border-dashed border-rose-200 bg-white p-6 text-center text-sm text-slate-500">Belum ada petugas yang tersedia saat ini. Silakan coba beberapa saat lagi atau pilih booking terjadwal.</p>}
      <div className="mt-4 space-y-3">
        {data?.doctors.map((d) => {
          const k = `${d.petugas_nama}|${d.service_id}`;
          return (
            <div key={k} data-testid={`now-doctor-${d.service_id}`} className="rounded-2xl border border-rose-100 bg-white p-4">
              <div className="flex items-start justify-between gap-2">
                <div><p className="font-bold text-slate-800">{d.petugas_nama}</p><p className="text-xs text-slate-500">{d.layanan} · {d.durasi} menit</p></div>
                <p className="font-heading font-extrabold text-[#DB2777]">{formatRp(d.harga)}</p>
              </div>
              {pick === k ? <BookForm d={d} tcText={tcText} /> : <button data-testid={`now-pick-${d.service_id}`} onClick={() => setPick(k)} className="mt-3 rounded-full bg-emerald-600 px-4 py-1.5 text-xs font-bold text-white">Konsultasi Sekarang</button>}
            </div>
          );
        })}
      </div>
    </div>
  );
}

const PH = {
  hold: ["Menunggu pembayaran", "Selesaikan pembayaran sebelum waktu habis agar tempat tidak dilepas."],
  await_accept: ["Pembayaran terverifikasi — menunggu dokter menerima", "Tautan ruang konsultasi dikirim via WhatsApp. Sesi dimulai saat dokter menerima."],
  active: ["Sesi berlangsung", "Buka ruang konsultasi melalui tautan yang dikirim via WhatsApp."],
  ended: ["Sesi selesai", "Terima kasih. Riwayat tetap dapat dibaca melalui tautan ruang konsultasi."],
  expired: ["Waktu pembayaran habis", "Tempat telah dilepas. Bila Anda sudah membayar, pembayaran tetap tercatat dan Admin GBS akan menindaklanjuti."],
  no_response: ["Dokter belum merespons", "Pembayaran Anda aman. Admin GBS akan menghubungi Anda untuk ganti dokter, jadwal ulang, atau refund."],
  needs_admin: ["Pembayaran diterima — perlu tindak lanjut admin", "Tempat sudah dilepas sebelum pembayaran masuk. Admin GBS akan menghubungi Anda (jadwal ulang/refund)."],
  scheduled: ["Dialihkan ke konsultasi terjadwal", "Admin GBS akan mengonfirmasi jadwal melalui WhatsApp."],
};

export function ConsultNowStatus() {
  const { token } = useParams();
  const [s, setS] = useState(null);
  const [, tick] = useState(0);
  useEffect(() => {
    const load = () => axios.get(`${API}/public/consult-now/status/${token}`).then((r) => setS({ ...r.data, skew: r.data.server_ts - Date.now() / 1000 })).catch(() => setS(false));
    load(); const a = setInterval(load, 10000); const b = setInterval(() => tick((x) => x + 1), 1000);
    return () => { clearInterval(a); clearInterval(b); };
  }, [token]);
  if (s === false) return <p className="p-6 text-center text-sm text-slate-500" data-testid="now-status-notfound">Konsultasi tidak ditemukan.</p>;
  if (!s) return <p className="p-6 text-center text-sm text-slate-400">Memuat…</p>;
  const [title, desc] = PH[s.phase] || [s.phase, ""];
  const rem = (ts) => Math.max(0, Math.round((ts || 0) - (Date.now() / 1000 + s.skew)));
  const bad = ["expired", "no_response", "needs_admin"].includes(s.phase);
  return (
    <div className="mx-auto min-h-screen max-w-lg bg-[#FFF7FA] p-4" data-testid="now-status-page">
      <div className="rounded-2xl border border-rose-100 bg-white p-5">
        <p className="text-xs text-slate-500">{s.layanan} · {s.petugas}</p>
        <p data-testid="now-status-title" className={`mt-1 flex items-center gap-1.5 font-heading text-lg font-extrabold ${bad ? "text-amber-700" : "text-emerald-700"}`}>{bad ? <Warning size={20} /> : <CheckCircle size={20} />} {title}</p>
        <p className="mt-1 text-sm text-slate-600">{desc}</p>
        {bad && <p data-testid="now-status-admin-hours" className="mt-2 text-[11px] text-slate-500">Tindak lanjut admin di luar jam kerja dapat menunggu jam operasional.</p>}
        {s.phase === "hold" && (
          <>
            <p data-testid="now-pay-countdown" className="mt-3 flex items-center gap-1 text-sm font-bold text-rose-600"><Timer size={16} /> {mmss(rem(s.pay_deadline_ts))}</p>
            <Link data-testid="now-pay-link" to={`/bayar/${token}`} className="mt-3 inline-block rounded-full bg-[#EC4899] px-5 py-2 text-sm font-bold text-white">Bayar {formatRp(s.total)}</Link>
            <p className="mt-2 text-[11px] text-slate-500">Transfer manual menunggu verifikasi admin; konsultasi tidak dijamin langsung dimulai.</p>
          </>
        )}
        {s.phase === "await_accept" && <p data-testid="now-accept-countdown" className="mt-3 flex items-center gap-1 text-sm font-bold text-emerald-700"><Timer size={16} /> Dokter diberi waktu {mmss(rem(s.accept_deadline_ts))}</p>}
      </div>
    </div>
  );
}
